import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Divider, Fade, Grid, Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import CustomizedBreadcrumbs from '../../../components/CustomBreadcrumbs';
import type { LoanOverViewType } from '../../../types/account';
import LoanSummary from '../LoanSummary';
import LoanInstallments from '../LoanInstallments';
import LoanDetails from '../LoanDetail';
import PendingPaymentDialog from '../../../components/PendingPaymentDialog';
import useStyles from './styles';
import { productTypeLabels } from '../../../components/CreditOverview';
import InsurancesSelector from '../../../components/InsurancesSelector';
import PayYourLoan from '../../payYourLoan';
import PrepayDialog from '../PrepayDialog';
import { formatDate, parseAmountWithCurrency } from '../../../utils/helpers';
import { useIsWidthDown } from '../../../utils/widthHooks';
import { getBnplMovementsStyles } from '../../../components/Bnpl/style';

const breadCrumbsElements = [
  { label: 'Resumen', hash: 'summary', icon: undefined },
  { label: 'Cuotas', hash: 'installments', icon: undefined },
  { label: 'Detalle', hash: 'detail', icon: undefined },
];

type LoanProps = {
    loanData: LoanOverViewType,
    token: string,
    defaultExpanded: boolean,
    minimalistView?: boolean,
    index: number,
}

export default function LoanOverview(props:LoanProps) {
  const {
    loanData, token, defaultExpanded, minimalistView = false,
    index,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useIsWidthDown('sm');
  const sx = getBnplMovementsStyles(theme);
  const {
    unpaidDelinquenciesAmount,
    unpaidCollectionFeesAmount,
  } = loanData;
  const [unpaidInstallmentAmount, setUnpaidInstallmentAmount] = React.useState(0);
  const [checkedInsurances, setCheckedInsurances] = React.useState<
  {[key:string]: boolean}>({});
  const [currentSection, setCurrentSection] = React.useState<'summary' | 'installments' | 'detail'>('summary');
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const handleSetCurrentSection = (section: 'summary' | 'installments' | 'detail' | string) => {
    if (['summary', 'installments', 'detail'].includes(section)) {
      setCurrentSection(section as 'summary' | 'installments' | 'detail');
    }
  };

  React.useEffect(() => {
    if (loanData
      && (loanData.hasDelinquency === true
        || loanData.unpaidCollectionFeesAmount > 0
        || loanData.unpaidDelinquenciesAmount > 0)) {
      let installmentDebtAmount = 0;
      for (let i = 0; i < loanData.installmentsDebts.length; i += 1) {
        installmentDebtAmount += loanData.installmentsDebts[i].amount;
      }
      setUnpaidInstallmentAmount(installmentDebtAmount);
    }
    if (loanData) {
      const checked: {[key:string]: boolean} = {};
      for (let i = 0; i < loanData.insurances.length; i += 1) {
        checked[loanData.insurances[i].type] = true;
      }
      setCheckedInsurances(checked);
    }
  }, [loanData]);

  const partnerOrStoreLabel = loanData.productType === 'bnpl' && (loanData.partner?.name !== 'WOM') && loanData.partnerEntityName
    ? loanData.partnerEntityName.replaceAll('SPA', '').replaceAll('S.A.', '').replaceAll('spa', '')
    : loanData.partner.name;
  return (
    <Accordion
      style={{
        marginInline: minimalistView ? 0 : '1%',
        marginBlock: minimalistView ? '3px' : '1%',
        borderRadius: minimalistView ? 10 : 15,
        border: isMobile ? `1px solid ${theme.palette.grey[200]}` : 'none',
        backgroundColor: minimalistView && !isMobile ? theme.palette.grey[50] : undefined,
        boxShadow: minimalistView ? 'none' : undefined,
      }}
      defaultExpanded={defaultExpanded}
      onChange={(event, expanded) => {
        setIsExpanded(expanded);
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreOutlinedIcon sx={{ color: theme.palette.black.main }} />}
        aria-controls="panel1-content"
        id="panel1-header"
        style={{
          borderRadius: 20,
          marginTop: minimalistView ? 2 : 5,
          minHeight: isMobile ? 80 : 120,
        }}
        sx={{
          '& .MuiAccordionSummary-content': {
            marginBottom: isMobile ? '15px' : undefined,
            marginLeft: isMobile ? '5px' : undefined,
            marginRight: isMobile ? '7.5px' : undefined,
          },
        }}
      >
        {!minimalistView ? (
          <Typography
            component="div"
            variant="h5"
            sx={{
              display: 'flex',
              placeContent: 'space-between',
              alignContent: 'center',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', columnGap: 10 }}>
              <div style={{
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}
              >
                <b style={{ alignSelf: 'start', paddingTop: 3 }}>
                  {productTypeLabels[loanData.productType]}
                </b>
                <Typography
                  variant="caption"
                  component="div"
                  color="secondary"
                  sx={{
                    paddingLeft: 0.5,
                    paddingTop: 1.5,
                    alignSelf: 'start',
                    width: '100%',
                  }}
                >
                  Fecha de otorgamiento:
                  {isMobile ? <br /> : ' '}
                  {formatDate(loanData.loan.createdAt)}
                </Typography>
              </div>
              {Object.keys(checkedInsurances).length > 0
            && (
            <InsurancesSelector
              pageCall={{ category: 'PU - TU CREDITO', name: 'VER SEGUROS' }}
              amount={loanData.loanAmount}
              periods={loanData.periods}
              handleSelectedAmountMarks={() => {}}
              handleSetCheckedInsurances={() => {}}
              showSimulator={false}
              availableInsurances={loanData.insurances}
              checkedInsurances={checkedInsurances}
              hideFooter
              hideTitle
              iconSize="small"
              readOnly
              currency={loanData.currency}
            />
            )}
            </div>

            <div style={{ alignSelf: 'center', textAlign: 'right' }}>
              <b>
                {partnerOrStoreLabel}
              </b>
              {isMobile ? <br /> : ' '}
              <b style={{ color: theme.palette.secondary.main }}>
                [
                {loanData.paymentMethod.toUpperCase()}
                ]
              </b>
            </div>
          </Typography>
        ) : (
          <Box sx={{
            width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
          >
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, width: isExpanded ? '100%' : '70%', textAlign: 'left',
            }}
            >
              <Box
                component="span"
                aria-hidden
                sx={{
                  ...sx.avatar,
                  ...(index % 2 === 0 ? sx.avatarMuted : sx.avatarTint),
                }}
              >
                {partnerOrStoreLabel.slice(0, 2).toUpperCase()}
              </Box>
              <Typography
                variant="body2"
                color="primary"
                fontWeight="bold"
                textAlign="left"
              >
                {partnerOrStoreLabel}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ width: isExpanded ? '0%' : '29%', textAlign: 'right', visibility: isExpanded ? 'hidden' : 'visible' }}
            >
              {parseAmountWithCurrency(loanData.currency, loanData.totalAmount, loanData.currency.abbreviation === 'CLF')}
            </Typography>
          </Box>
        )}

      </AccordionSummary>
      <AccordionDetails>
        <Grid
          container
          justifyContent="flex-end"
          rowGap={5}
        >
          <Grid
            item
            xs={12}
            lg={11}
          >
            <CustomizedBreadcrumbs
              elements={breadCrumbsElements}
              separator="-"
              currentSection={currentSection}
              onClick={handleSetCurrentSection}
              isMobile={isMobile}
            />
          </Grid>
          <Fade in {... ({ timeout: 2500 })}>
            <Grid
              container
              item
              xs={12}
              lg={11}
            >
              <Grid
                item
                xs={12}
                lg={11}
              >
                {currentSection === 'summary'
                    && (
                    <>
                      <LoanSummary loanData={loanData} minimalistView={minimalistView} />
                      <div style={{ textAlign: 'center' }}>
                        {Object.keys(checkedInsurances).length > 0
            && (
              <>
                <Divider style={{ marginTop: 10 }} />
                <InsurancesSelector
                  pageCall={{ category: 'PU - TU CREDITO', name: 'SEGUROS PRODUCTO' }}
                  amount={loanData.loanAmount}
                  periods={loanData.periods}
                  handleSelectedAmountMarks={() => {}}
                  handleSetCheckedInsurances={() => {}}
                  showSimulator
                  availableInsurances={loanData.insurances}
                  checkedInsurances={checkedInsurances}
                  hideFooter
                  readOnly
                  currency={loanData.currency}
                />
                <Divider style={{ marginTop: 10 }} />
              </>
            )}
                      </div>
                    </>
                    )}
              </Grid>
              {currentSection === 'installments'
                  && (
                  <Grid
                    item
                    xs={12}
                    lg={11}
                    xl={12}
                  >
                    <LoanInstallments
                      loanInstallments={loanData.installments}
                      loanUnpaidDelinquencies={loanData.unpaidDelinquencies}
                      loanUnpaidCollectionFees={loanData.unpaidCollectionFees}
                      loanPaidDelinquencies={loanData.paidDelinquencies}
                      loanPaidCollectionFees={loanData.paidCollectionFees}
                      loanCurrency={loanData.currency}
                    />
                  </Grid>
                  )}
              {currentSection === 'detail'
                  && (
                  <Grid
                    item
                    xs={12}
                    lg={11}
                    xl={11}
                  >
                    <LoanDetails
                      loanNumber={loanData.loan.number}
                      installmentAmount={loanData.installmentAmount}
                      lastInstallmentAmount={loanData.lastInstallmentAmount}
                      periods={loanData.periods}
                      paidQuotes={loanData.paidQuotes}
                      unpaidAmount={loanData.unpaidAmount}
                      cae={loanData.cae}
                      interestRate={loanData.interestRate}
                      toPayQuotes={loanData.toPayQuotes}
                      currency={loanData.currency}
                    />
                  </Grid>
                  )}
              <Grid
                item
                xs={12}
                lg={11}
              >
                <div className={classes.actionButtons}>
                  <PayYourLoan
                    token={token}
                    loanId={loanData.loan.id}
                    loanCurrency={loanData.currency}
                    unpaidAmount={loanData.unpaidAmount}
                    paidAmount={loanData.totalAmount - loanData.unpaidAmount}
                    unpaidTotalAmount={
                      loanData.unpaidAmount
                      + unpaidDelinquenciesAmount
                      + unpaidCollectionFeesAmount
                    }
                    productType={loanData.productType}
                    pageCategory="PU - TU CREDITO"
                  />
                  <PrepayDialog
                    unpaidAmount={loanData.unpaidAmount}
                    paidAmount={loanData.totalAmount - loanData.unpaidAmount}
                    loanState={loanData.loan.state}
                    productType={loanData.productType}
                    token={token}
                    loanId={loanData.loan.id}
                    periods={loanData.periods}
                    refinancePolicy={loanData.refinancePolicy}
                    currency={loanData.currency}
                  />
                  <PendingPaymentDialog
                    paymentMethod={loanData.paymentMethod}
                    hasDelinquency={loanData.hasDelinquency}
                    unpaidDelinquencyAmount={unpaidDelinquenciesAmount}
                    unpaidInstallmentAmount={unpaidInstallmentAmount}
                    unpaidCollectionFeeAmount={unpaidCollectionFeesAmount}
                    token={token}
                    loanId={loanData.loan.id}
                    paidAmount={loanData.totalAmount - loanData.unpaidAmount}
                    productType={loanData.productType}
                    unpaidAmount={loanData.unpaidAmount}
                    unpaidTotalAmount={
                      loanData.unpaidAmount
                      + unpaidDelinquenciesAmount
                      + unpaidCollectionFeesAmount
                    }
                    currency={loanData.currency}
                    partnerOrStoreLabel={partnerOrStoreLabel}
                  />
                </div>
              </Grid>
            </Grid>
          </Fade>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

LoanOverview.defaultProps = {
  minimalistView: false,
};
